import { useCallback, useEffect, useRef, useState } from "react";
import { describeMicrophoneError } from "@/lib/speechErrors";
import i18n from "@/i18n";

export type RecordingStatus = "idle" | "recording" | "stopped";

export interface AudioRecorderResult {
  /** Current recording status */
  status: RecordingStatus;
  /**
   * Start recording audio from the microphone.
   * Rejects with a user-displayable message if the microphone is
   * unavailable or recording is unsupported; `status` stays `"idle"` then.
   */
  startRecording: () => Promise<void>;
  /** Stop recording and produce the audio blob */
  stopRecording: () => void;
  /** The assembled audio Blob after stopping, null while recording or idle */
  audioBlob: Blob | null;
  /** Reset state back to idle so a new recording can begin */
  reset: () => void;
  /** The microphone stream while recording (e.g. for a level meter) */
  stream: MediaStream | null;
}

// audio/webm n'est pas supporté partout (Safari iOS => audio/mp4) : on prend
// le premier format supporté au lieu de laisser MediaRecorder throw.
const MIME_CANDIDATES = ["audio/webm", "audio/mp4", "audio/ogg"];

function pickSupportedMimeType(): string | undefined {
  if (typeof MediaRecorder.isTypeSupported !== "function") return undefined;
  return MIME_CANDIDATES.find((m) => MediaRecorder.isTypeSupported(m));
}

export function useAudioRecorder(): AudioRecorderResult {
  const [status, setStatus] = useState<RecordingStatus>("idle");
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  /** Coupe le micro (et l'indicateur d'enregistrement du navigateur). */
  const releaseStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setStream(null);
  }, []);

  const startRecording = useCallback(async () => {
    try {
      setAudioBlob(null);
      chunksRef.current = [];

      if (typeof MediaRecorder === "undefined") {
        throw new Error(i18n.t("core:speech.recordingUnsupported"));
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      setStream(stream);

      const mimeType = pickSupportedMimeType();
      const mediaRecorder = new MediaRecorder(
        stream,
        mimeType ? { mimeType } : undefined,
      );
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event: BlobEvent) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, {
          type: mediaRecorder.mimeType || mimeType || "audio/webm",
        });
        setAudioBlob(blob);
        setStatus("stopped");
        releaseStream();
      };

      mediaRecorder.start();
      setStatus("recording");
    } catch (err) {
      // Libère un éventuel flux micro acquis avant l'échec (ex. MediaRecorder
      // qui throw) pour ne pas laisser l'indicateur micro du navigateur allumé.
      releaseStream();

      const message =
        err instanceof Error && !(err instanceof DOMException)
          ? err.message
          : describeMicrophoneError(err);
      setStatus("idle");
      throw new Error(message, { cause: err });
    }
  }, [releaseStream]);

  const stopRecording = useCallback(() => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === "recording"
    ) {
      mediaRecorderRef.current.stop();
    }
  }, []);

  const reset = useCallback(() => {
    // Stop any ongoing recording. `onstop` is detached first: it fires
    // asynchronously and would otherwise publish the discarded recording as
    // `audioBlob` after the reset.
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === "recording"
    ) {
      mediaRecorderRef.current.onstop = null;
      mediaRecorderRef.current.stop();
    }
    releaseStream();
    mediaRecorderRef.current = null;
    chunksRef.current = [];

    setStatus("idle");
    setAudioBlob(null);
  }, [releaseStream]);

  // Hôte démonté en plein enregistrement (panneau de chat refermé) : le micro
  // ne doit pas rester ouvert derrière lui.
  useEffect(() => reset, [reset]);

  return {
    status,
    startRecording,
    stopRecording,
    audioBlob,
    reset,
    stream,
  };
}
